import type { Purchase } from "./ads";
import type { ConversionEvent } from "./conversions";
import type { BillingInterval, BillingPlanList, BillingTier } from "./api/billing";

/**
 * What a return from Stripe Checkout says was bought, so an ad's
 * purchase can be counted with its value. The checkout hooks write it
 * into the return address, and Stripe puts its session id where
 * {@link STRIPE_SESSION} stands. Only a return with that id is a
 * purchase: anyone can open the address without paying.
 */

/** Stripe's placeholder for the Checkout session id in a return address. Never escaped. */
export const STRIPE_SESSION = "{CHECKOUT_SESSION_ID}";

export type Bought =
    | {
          kind: "plan";
          /** Null when the return names none, as an older Checkout's did. */
          tier: Exclude<BillingTier, "free"> | null;
          interval: BillingInterval | null;
          session: string;
      }
    | { kind: "pack"; tailors: number | null; session: string };

/** The query a plan's Checkout comes back with. */
export function planReturnQuery(tier: Exclude<BillingTier, "free">, interval: BillingInterval) {
    return `?tier=${tier}&interval=${interval}&session=${STRIPE_SESSION}`;
}

/** The query a pack's Checkout comes back with: `pack=paid` first, as the page reads it. */
export function packReturnQuery(tailors: number) {
    return `?pack=paid&tailors=${tailors}&session=${STRIPE_SESSION}`;
}

/** The return query's own names, which the pricing page drops once read. */
export const RETURN_PARAMS = ["tier", "interval", "tailors", "session"] as const;

const TIERS: ReadonlySet<string> = new Set(["pro", "max"]);
const INTERVALS: ReadonlySet<string> = new Set(["month", "quarter", "year"]);

/** Stripe's session id, when Stripe filled it in: a Checkout session's id and nothing else. */
function sessionOf(params: URLSearchParams): string | null {
    const session = params.get("session");
    return session && /^cs_[A-Za-z0-9_]{1,200}$/.test(session) ? session : null;
}

/**
 * The plan a return to /subscribed names, or null without Stripe's
 * session id. Counted even unnamed, without its value.
 */
export function planBought(params: URLSearchParams): Bought | null {
    const session = sessionOf(params);
    if (!session) return null;
    const tier = params.get("tier") ?? "";
    const interval = params.get("interval") ?? "";
    const named = TIERS.has(tier) && INTERVALS.has(interval);
    return {
        kind: "plan",
        tier: named ? (tier as Exclude<BillingTier, "free">) : null,
        interval: named ? (interval as BillingInterval) : null,
        session,
    };
}

/** The pack a return to /pricing names, or null when it is not one or has no session id. */
export function packBought(params: URLSearchParams): Bought | null {
    const session = sessionOf(params);
    if (params.get("pack") !== "paid" || !session) return null;
    const tailors = Number(params.get("tailors"));
    return {
        kind: "pack",
        tailors: Number.isInteger(tailors) && tailors > 0 ? tailors : null,
        session,
    };
}

/**
 * What the ads are told: the price on sale for what was bought, when
 * the plans are read and it is among them, no value otherwise; and for
 * a plan the return names, its tier and interval.
 */
export function purchaseOf(bought: Bought, list: BillingPlanList | undefined): Purchase {
    const price = !list
        ? undefined
        : bought.kind === "plan"
          ? list.plans
                .find((plan) => plan.tier === bought.tier)
                ?.prices.find((entry) => entry.interval === bought.interval)
          : list.packs.find((pack) => pack.tailors === bought.tailors);
    return {
        kind: bought.kind,
        value: price ? price.unit_amount / 100 : null,
        currency: price ? price.currency.toUpperCase() : null,
        transactionId: bought.session,
        planId:
            bought.kind === "plan" && bought.tier && bought.interval
                ? `${bought.tier}_${bought.interval}`
                : null,
    };
}

/**
 * What PostHog is told: the same price as the ads, and what was bought by
 * name, never Stripe's session id.
 */
export function purchaseEventOf(
    bought: Bought,
    purchase: Purchase,
): Extract<ConversionEvent, { name: "purchase_completed" }> {
    return {
        name: "purchase_completed",
        kind: bought.kind,
        item:
            bought.kind === "plan"
                ? purchase.planId
                : bought.tailors === null
                  ? null
                  : String(bought.tailors),
        value: purchase.value,
        currency: purchase.currency,
    };
}
